import React, { useMemo, useState } from "react";
import "./LoanSimulatorBelgium.css";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  ArrowRight,
  Bookmark,
  Download,
  Euro,
  Flame,
  HeartPulse,
  MapPin,
  Shield,
  Users,
  Wallet,
} from "lucide-react";
import {
  SimulatorHeader,
  ReuseCard,
  FieldWithIcon,
  FieldLabel,
} from "../components/SimulatorParts";
import useSimulatorFormat from "../components/useSimulatorFormat";
import { downloadSimulationWorkbook } from "../utils/simulationExcel";
import {
  asText,
  clampNumber,
  readSimulationWorkbook,
} from "../utils/simulationExcelImport";
import {
  BELGIUM_LOAN_PARAMS,
  FREQUENCIES,
  MAX_TERM_YEARS,
  MIN_TERM_YEARS,
  REGIONS,
  simulateLoanBelgium,
} from "./LoanSimulatorBelgiumCalc";

const INSURANCES = [
  { key: "lifeBorrower", icon: HeartPulse, hasDuration: true },
  { key: "lifeCoBorrower", icon: Users, hasDuration: true },
  { key: "fire", icon: Flame, hasDuration: false },
];

const EMPTY_INSURANCE = { premium: "", frequency: "monthly", durationYears: "" };

function InsuranceFields({ insurance, value, termYears, onChange, t }) {
  const Icon = insurance.icon;
  const update = (field) => (e) => onChange({ ...value, [field]: e.target.value });

  return (
    <div className="simulator__subcard">
      <span className="simulator__subcard-title">
        <Icon size={14} /> {t(insurance.key)}
      </span>
      <div className="simulator__subgrid simulator__subgrid--3">
        <div className="simulator__field">
          <FieldLabel tip={t("tips.premium")}>{t("premium")}</FieldLabel>
          <FieldWithIcon icon={Euro}>
            <input
              type="number"
              min="0"
              step="0.01"
              value={value.premium}
              onChange={update("premium")}
              placeholder="0"
            />
          </FieldWithIcon>
        </div>
        <div className="simulator__field">
          <FieldLabel tip={t("tips.frequency")}>{t("frequency")}</FieldLabel>
          <FieldWithIcon>
            <select value={value.frequency} onChange={update("frequency")}>
              {FREQUENCIES.map((f) => (
                <option key={f} value={f}>{t(`frequencies.${f}`)}</option>
              ))}
            </select>
          </FieldWithIcon>
        </div>
        {insurance.hasDuration && (
          <div className="simulator__field">
            <FieldLabel tip={t("tips.duration")}>{t("duration")}</FieldLabel>
            <FieldWithIcon>
              <input
                type="number"
                min="1"
                max={termYears}
                step="1"
                value={value.durationYears}
                onChange={update("durationYears")}
                placeholder={String(termYears)}
              />
            </FieldWithIcon>
          </div>
        )}
      </div>
    </div>
  );
}

function CostRow({ label, value, className = "" }) {
  return (
    <div className={`simulator__taxes-row ${className}`}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}

export default function LoanSimulatorBelgium({ onBack, onContinue }) {
  const { t, i18n } = useTranslation("loan-simulator-belgium");

  const [instalment, setInstalment] = useState("");
  const [termYears, setTermYears] = useState(25);
  const [region, setRegion] = useState("flanders");
  const [insurances, setInsurances] = useState({
    lifeBorrower: { ...EMPTY_INSURANCE },
    lifeCoBorrower: { ...EMPTY_INSURANCE },
    fire: { ...EMPTY_INSURANCE, frequency: "annual" },
  });

  const [scheduleOpen, setScheduleOpen] = useState(false);

  const result = useMemo(
    () =>
      simulateLoanBelgium({
        instalment,
        termYears,
        region,
        insurances,
      }),
    [
      instalment,
      termYears,
      region,
      insurances,
    ],
  );

  const { money, percent } = useSimulatorFormat();

  const annualRate = Math.pow(1 + BELGIUM_LOAN_PARAMS.monthlyInterestRate, 12) - 1;

  const handleDownload = async () => {
    const currentInputs = {
      instalment,
      termYears,
      region,
      insurances,
    };

    /*
     * Recalculate at the moment of export so the Excel file is
     * guaranteed to match the values currently shown in the form.
     */
    const currentResult =
      simulateLoanBelgium(
        currentInputs,
      );

    if (!currentResult) {
      return;
    }

    await downloadSimulationWorkbook({
      simulationType: "LOAN_CAPACITY",
      region: "FLANDERS",
      language:
        i18n.resolvedLanguage ||
        i18n.language ||
        "en",
      calculatorVersion:
        BELGIUM_LOAN_PARAMS.version,
      inputs:
        currentInputs,
      results:
        currentResult,
    });
  };

  const handleApplyWorkbook = async (
    file,
  ) => {
    const { inputs } =
      await readSimulationWorkbook(
        file,
        {
          expectedSimulationType:
            "LOAN_CAPACITY",
          expectedRegion:
            "FLANDERS",
        },
      );

    if (
      Object.prototype.hasOwnProperty.call(
        inputs,
        "instalment",
      )
    ) {
      setInstalment(
        asText(
          inputs.instalment,
        ),
      );
    }

    if (
      Object.prototype.hasOwnProperty.call(
        inputs,
        "termYears",
      )
    ) {
      setTermYears(
        clampNumber(
          inputs.termYears,
          MIN_TERM_YEARS,
          MAX_TERM_YEARS,
          25,
        ),
      );
    }

    if (
      inputs.region &&
      REGIONS.includes(
        String(
          inputs.region,
        ).toLowerCase(),
      )
    ) {
      setRegion(
        String(
          inputs.region,
        ).toLowerCase(),
      );
    }

    if (
      inputs.insurances &&
      typeof inputs.insurances ===
        "object"
    ) {
      setInsurances(
        (current) => {
          const next = {
            ...current,
          };

          INSURANCES.forEach(
            ({ key }) => {
              const imported =
                inputs.insurances?.[
                  key
                ];

              if (
                !imported ||
                typeof imported !==
                  "object"
              ) {
                return;
              }

              next[key] = {
                ...current[key],
                premium:
                  asText(
                    imported.premium,
                    current[key]
                      ?.premium ||
                      "",
                  ),
                frequency:
                  FREQUENCIES.includes(
                    imported.frequency,
                  )
                    ? imported.frequency
                    : current[key]
                        ?.frequency ||
                      "monthly",
                durationYears:
                  asText(
                    imported.durationYears,
                    current[key]
                      ?.durationYears ||
                      "",
                  ),
              };
            },
          );

          return next;
        },
      );
    }
  };

  return (
    <div className="simulator">
      {/* ---------- LEFT COLUMN ---------- */}
      <div className="simulator__left-col">
        <SimulatorHeader eyebrow={t("eyebrow")} title={t("title")} subtitle={t("subtitle")} />

        <ReuseCard
          title={t("reuse-title")}
          description={t("reuse-desc")}
          browseLabel={t("browse")}
          applyLabel={t("upload-apply")}
          onApply={handleApplyWorkbook}
        />

        <div className="simulator__card simulator__form-card">
          <div className="simulator__section-header">
            <div className="simulator__section-title">
              <Wallet size={16} />
              <span>{t("your-loan")}</span>
            </div>
            <button type="button" className="simulator__btn-outline simulator__btn-outline--sm">
              <Bookmark size={14} /> {t("load-saved")}
            </button>
          </div>

          <div className="simulator__grid">
            <div className="simulator__field">
              <FieldLabel tip={t("tips.instalment")}>{t("instalment")}</FieldLabel>
              <FieldWithIcon icon={Euro} unit="€">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={instalment}
                  onChange={(e) => setInstalment(e.target.value)}
                  placeholder="0"
                />
              </FieldWithIcon>
            </div>

            <div className="simulator__field">
              <FieldLabel tip={t("tips.region")}>{t("region")}</FieldLabel>
              <FieldWithIcon icon={MapPin}>
                <select value={region} onChange={(e) => setRegion(e.target.value)}>
                  {Object.keys(REGIONS).map((r) => (
                    <option key={r} value={r}>{t(`regions.${r}`)}</option>
                  ))}
                </select>
              </FieldWithIcon>
            </div>

            <div className="simulator__field simulator__field--slider simulator__field--wide">
              <FieldLabel tip={t("tips.term")}>{t("term")}</FieldLabel>
              <div className="simulator__slider-row">
                <input
                  type="range"
                  min={MIN_TERM_YEARS}
                  max={MAX_TERM_YEARS}
                  value={termYears}
                  onChange={(e) => setTermYears(Number(e.target.value))}
                />
                <span className="simulator__slider-value">{t("years", { count: termYears })}</span>
              </div>
              <div className="simulator__slider-limits">
                <span>{MIN_TERM_YEARS}</span>
                <span>{MAX_TERM_YEARS}</span>
              </div>
            </div>
          </div>

          <div className="simulator__section-header">
            <div className="simulator__section-title">
              <Shield size={16} />
              <span>{t("insurance")}</span>
            </div>
          </div>
          <p className="simulator__hint">{t("insurance-hint")}</p>

          <div className="simulator__subcards">
            {INSURANCES.map((insurance) => (
              <InsuranceFields
                key={insurance.key}
                insurance={insurance}
                value={insurances[insurance.key]}
                termYears={termYears}
                onChange={(value) => setInsurances((prev) => ({ ...prev, [insurance.key]: value }))}
                t={t}
              />
            ))}
          </div>

          <div className="simulator__footer">
            <button type="button" className="simulator__btn-back" onClick={onBack}>
              <ArrowLeft size={16} /> {t("back")}
            </button>
            <button type="button" className="simulator__btn-continue" onClick={onContinue}>
              {t("save-continue")} <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* ---------- RESULT ---------- */}
      <div className="simulator__card simulator__result-card">
        <div className="simulator__tags">
          <span className="simulator__tag">{t("tag-fixed")}</span>
          <span className="simulator__tag">{t("tag-annual-rate", { rate: percent(annualRate) })}</span>
          <span className="simulator__tag">{t("tag-term", { count: termYears })}</span>
          <span className="simulator__tag simulator__tag--badge">{t("tag-indicative")}</span>
          {result && (
            <span className="simulator__tag">{t("tag-aprc", { rate: percent(result.taeg) })}</span>
          )}
        </div>

        <label className="simulator__result-label">{t("borrowing-capacity")}</label>
        <span className="simulator__result-value">{result ? money(result.loanAmount) : "— €"}</span>

        {!result ? (
          <p className="simulator__empty-result">{t("empty-result")}</p>
        ) : (
          <>
            <div className="simulator__mini-cards">
              <div className="simulator__mini-card">
                <span className="simulator__mini-label">{t("monthly-instalment")}</span>
                <span className="simulator__mini-value">{money(result.instalment)}</span>
              </div>
              <div className="simulator__mini-card">
                <span className="simulator__mini-label">{t("aprc")}</span>
                <span className="simulator__mini-value">{percent(result.taeg)}</span>
              </div>
            </div>

            <div className="simulator__rows">
              <div className="simulator__row">
                <span>{t("monthly-rate")}</span>
                <span>{percent(result.monthlyInterestRate, 4)}</span>
              </div>
              <div className="simulator__row">
                <span>{t("annual-rate")}</span>
                <span>{percent(result.annualInterestRate)}</span>
              </div>
              <div className="simulator__row">
                <span>{t("loan-term")}</span>
                <span>{t("term-value", { years: result.termYears, months: result.termMonths })}</span>
              </div>
              <div className="simulator__row">
                <span>{t("registration-amount")}</span>
                <span>{money(result.registrationAmount)}</span>
              </div>
            </div>

            {/* One-off costs */}
            <div className="simulator__taxes-box">
              <span className="simulator__taxes-title">{t("one-off-costs")}</span>
              <CostRow label={t("registration-duty")} value={money(result.notary.registrationDuty)} />
              <CostRow label={t("mortgage-duty")} value={money(result.notary.mortgageDuty)} />
              <CostRow label={t("retribution")} value={money(result.notary.retribution)} />
              <CostRow label={t("administrative-fee")} value={money(result.notary.administrativeFee)} />
              <CostRow label={t("writing-fee")} value={money(result.notary.writingFee)} />
              <CostRow label={t("vat")} value={money(result.notary.vat)} />
              <CostRow label={t("annex-fee")} value={money(result.notary.annexFee)} />
              <CostRow
                label={t("notary-fees")}
                value={money(result.notary.total)}
                className="simulator__taxes-row--subtotal"
              />
              <CostRow label={t("regional-contribution")} value={money(result.regionalContribution)} />
              <CostRow label={t("valuation-fee")} value={money(result.valuationFee)} />
              <CostRow label={t("file-fee")} value={money(result.fileFee)} />
              {result.extraCosts !== 0 && (
                <CostRow label={t("extra-costs")} value={money(result.extraCosts)} />
              )}
              {result.oneOffItems.map((item) => (
                <CostRow
                  key={item.key}
                  label={t("one-off-premium", { name: t(item.key) })}
                  value={money(item.premium)}
                />
              ))}
              <CostRow
                label={t("total-one-off")}
                value={money(result.oneOffCosts)}
                className="simulator__taxes-row--total"
              />
            </div>

            {/* Periodic costs */}
            {result.periodicItems.length > 0 && (
              <div className="simulator__taxes-box">
                <span className="simulator__taxes-title">{t("periodic-costs")}</span>
                {result.periodicItems.map((item) => (
                  <div className="simulator__taxes-row" key={item.key}>
                    <span>
                      {t(item.key)}
                      <span className="simulator__row-detail">
                        {t("periodic-detail", {
                          amount: money(item.premium),
                          frequency: t(`frequencies.${item.frequency}`).toLowerCase(),
                          years: item.durationYears,
                        })}
                      </span>
                    </span>
                    <span>{money(item.total)}</span>
                  </div>
                ))}
                <CostRow
                  label={t("total-periodic")}
                  value={money(result.periodicCosts)}
                  className="simulator__taxes-row--total"
                />
              </div>
            )}

            {/* Total cost */}
            <div className="simulator__taxes-box">
              <span className="simulator__taxes-title">{t("total-cost")}</span>
              <CostRow label={t("loan-amount")} value={money(result.loanAmount)} />
              <CostRow label={t("total-interest")} value={money(result.totalInterest)} />
              <CostRow label={t("total-costs")} value={money(result.totalCosts)} />
              <CostRow
                label={t("total-payable")}
                value={money(result.totalPayable)}
                className="simulator__taxes-row--total"
              />
            </div>

            {/* Repayment schedule */}
            <details
              className="loan-be__schedule"
              onToggle={(e) => setScheduleOpen(e.currentTarget.open)}
            >
              <summary>{t("show-schedule")}</summary>
              {scheduleOpen && (
              <div className="loan-be__schedule-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>{t("schedule.month")}</th>
                      <th>{t("schedule.balance")}</th>
                      <th>{t("schedule.capital")}</th>
                      <th>{t("schedule.interest")}</th>
                      <th>{t("schedule.instalment")}</th>
                      <th>{t("schedule.costs")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.schedule.map((row) => (
                      <tr key={row.month}>
                        <td>{row.month}</td>
                        <td>{money(row.balance)}</td>
                        <td>{money(row.capital)}</td>
                        <td>{money(row.interest)}</td>
                        <td>{money(row.instalment)}</td>
                        <td>{row.costs ? money(row.costs) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              )}
            </details>

            {!result.hasInsurance && <p className="simulator__note">{t("no-insurance-note")}</p>}
          </>
        )}

        <p className="simulator__disclaimer">
          {t("disclaimer")}
          <br />
          {BELGIUM_LOAN_PARAMS.version}
        </p>

        <button
          type="button"
          className="simulator__btn-download"
          onClick={handleDownload}
          disabled={!result}
        >
          <Download size={16} /> {t("download")}
        </button>
      </div>
    </div>
  );
}
